/**
 * BatchDatePickerModal Component
 *
 * Modal dialog for batch updating due dates and competence dates across multiple transactions.
 * Provides date pickers for both required due date and optional competence date,
 * with validation and real-time preview of affected transactions.
 *
 * @component
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const [selectedTx, setSelectedTx] = useState(new Set(['id1', 'id2']));
 *
 * <BatchDatePickerModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   selectedTransactions={selectedTx}
 *   onClearSelection={() => setSelectedTx(new Set())}
 * />
 * ```
 *
 * @remarks
 * - Due date is required, competence date is optional
 * - Validates required fields before submission
 * - Shows count of affected transactions in header
 * - Automatically clears selection after successful update
 * - Resets form state when modal closes
 *
 * @see {@link useBatchOperations} for the underlying batch update logic
 */

import { useState } from 'react';
import { Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useBatchOperations } from '@/hooks/useBatchOperations';
import { toast } from 'sonner';

/**
 * Props for BatchDatePickerModal component
 *
 * @interface BatchDatePickerModalProps
 * @property {boolean} open - Controls modal visibility
 * @property {function} onOpenChange - Callback when modal open state changes
 * @property {Set<string>} selectedTransactions - Set of transaction IDs to update
 * @property {function} onClearSelection - Callback to clear transaction selection after update
 */
export interface BatchDatePickerModalProps {
  /** Controls whether the modal is visible */
  open: boolean;
  /** Called when the modal should be opened or closed */
  onOpenChange: (open: boolean) => void;
  /** Set of transaction IDs to be updated */
  selectedTransactions: Set<string>;
  /** Called after successful update to clear the selection */
  onClearSelection: () => void;
}

export function BatchDatePickerModal({
  open,
  onOpenChange,
  selectedTransactions,
  onClearSelection,
}: BatchDatePickerModalProps) {
  const [dueDate, setDueDate] = useState('');
  const [competenceDate, setCompetenceDate] = useState('');
  const { batchUpdateDueDate, isUpdating } = useBatchOperations();

  const selectedIds = Array.from(selectedTransactions);
  const count = selectedIds.length;

  const handleConfirm = async () => {
    if (!dueDate) {
      toast.error('Selecione uma data de vencimento');
      return;
    }

    try {
      await batchUpdateDueDate(selectedIds, dueDate, competenceDate || undefined);
      onOpenChange(false);
      onClearSelection();

      // Reset form
      setDueDate('');
      setCompetenceDate('');
    } catch (error) {
      // Error handling is done by the hook
      console.error('Batch date update error:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setDueDate('');
    setCompetenceDate('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar datas em lote</DialogTitle>
          <DialogDescription>
            Altere as datas de vencimento e/ou competência de {count} {count === 1 ? 'transação' : 'transações'}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="due-date">
              Data de vencimento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={isUpdating}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="competence-date">
              Data de competência (opcional)
            </Label>
            <Input
              id="competence-date"
              type="date"
              value={competenceDate}
              onChange={(e) => setCompetenceDate(e.target.value)}
              disabled={isUpdating}
            />
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              <Calendar className="inline-block mr-1 h-4 w-4" />
              {count} {count === 1 ? 'transação será atualizada' : 'transações serão atualizadas'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!dueDate || isUpdating}
          >
            {isUpdating ? 'Atualizando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
